import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { DocumentationSlug } from '../lib/documentation';

type LoadState =
  | { status: 'loading'; html: null; message: string }
  | { status: 'ready'; html: string; message: null }
  | { status: 'forbidden'; html: null; message: string }
  | { status: 'error'; html: null; message: string };

function readHashToken(): string | null {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  return params.get('token');
}

function isDocumentationSlug(value: unknown): value is DocumentationSlug {
  return value === 'user' || value === 'developers';
}

function isDocsSwitchMessage(
  value: unknown,
): value is { type: 'mp-trend-docs-switch'; slug: DocumentationSlug } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'slug' in value &&
    value.type === 'mp-trend-docs-switch' &&
    isDocumentationSlug(value.slug)
  );
}

async function fetchDocumentationHtml(slug: DocumentationSlug, token: string): Promise<string> {
  return api.getText(`/api/docs/page/${slug}`, { 'X-MpTrend-Docs-Token': token }, false);
}

async function getFreshDocsToken(slug: DocumentationSlug): Promise<string> {
  const ticket = await api.post<{ token: string }>('/api/docs/open-token', { slug });
  return ticket.token;
}

export function DocumentationPage({ slug }: { slug: DocumentationSlug }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [state, setState] = useState<LoadState>({
    status: 'loading',
    html: null,
    message: 'Загружаем документацию',
  });

  const switchDocumentation = useCallback((nextSlug: DocumentationSlug) => {
    setState({ status: 'loading', html: null, message: 'Открываем раздел документации' });
    api
      .post<{ path: string; token: string }>('/api/docs/open-token', { slug: nextSlug })
      .then((ticket) => {
        const nextUrl = `${ticket.path}#token=${encodeURIComponent(ticket.token)}`;
        window.location.assign(nextUrl);
      })
      .catch((err) => {
        const forbidden =
          err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 404);
        setState({
          status: forbidden ? 'forbidden' : 'error',
          html: null,
          message: forbidden
            ? 'Этот раздел документации недоступен для вашей роли.'
            : 'Не удалось открыть раздел документации. Повторите попытку позже.',
        });
      });
  }, []);

  useEffect(() => {
    const hashToken = readHashToken();

    let cancelled = false;
    (hashToken ? Promise.resolve(hashToken) : getFreshDocsToken(slug))
      .then((token) => fetchDocumentationHtml(slug, token))
      .then((html) => {
        if (!cancelled) {
          window.history.replaceState(null, '', window.location.pathname);
          setState({ status: 'ready', html, message: null });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        window.history.replaceState(null, '', window.location.pathname);
        const forbidden =
          err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 404);
        setState({
          status: forbidden ? 'forbidden' : 'error',
          html: null,
          message: forbidden
            ? 'Документация доступна только авторизованным пользователям с подходящей ролью.'
            : 'Не удалось загрузить документацию. Повторите попытку позже.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isDocsSwitchMessage(event.data)) return;
      if (event.data.slug === slug) return;
      switchDocumentation(event.data.slug);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [slug, switchDocumentation]);

  if (state.status !== 'ready') {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-surface text-text-primary">
        <div className="w-full max-w-[460px] rounded-md border border-border-default/[0.1] bg-panel p-6 shadow-card">
          <div className="rubric mb-2">MP Trend</div>
          <h1 className="text-title font-strong">
            {state.status === 'loading' ? 'Документация' : 'Доступ к документации'}
          </h1>
          <p className="mt-3 text-body text-text-secondary">{state.message}</p>
        </div>
      </div>
    );
  }

  const html = state.html;

  return (
    <iframe
      ref={iframeRef}
      title="Документация MP Trend"
      srcDoc={html}
      className="block h-screen min-h-screen w-full border-0"
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
    />
  );
}
